/**
 * D10 — Review Your Donation.
 *
 * The last screen before money moves, so the total shown is re-fetched from
 * the server rather than carried forward. If the quote has expired the confirm
 * action is blocked until a fresh one arrives — the donor can never confirm a
 * price the server would not honour.
 *
 * Purpose is shown here; the artwork omitted it (gap G-04) even though it is
 * part of what the donor is agreeing to.
 */

import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';

import { formatDate, formatMoney, formatPeople } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { Card, CardDivider, CardRow } from '../../components/common/Card';
import { CheckoutHeader } from '../../components/donation/CheckoutHeader';
import { DonationSummaryCard } from '../../components/donation/DonationSummaryCard';
import { Glyph, LockIcon } from '../../components/common/Icon';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { Text } from '../../components/common/Text';
import {
  useAppConfig,
  useCreateDonation,
  useInitiatePayment,
  usePaymentMethods,
  useQuote,
} from '../../core/api/hooks';
import { toCreateRequest, useDonationStore } from '../donation/donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';
import { paymentReturnUrl } from '../../core/payments/hostedPage';

export function ReviewScreen({ navigation }: DonorScreenProps<'Review'>) {
  const t = useT();
  const pick = useLocalized();
  const draft = useDonationStore();
  const { data: config } = useAppConfig();
  const { data: methods } = usePaymentMethods();

  // Re-quote on arrival: the price the donor confirms is the current one.
  const freshQuote = useQuote(draft.meal?.id ?? null, draft.peopleCount, {
    type: draft.recipient.fulfilmentType,
    city: draft.recipient.address?.city ?? null,
  });
  const setQuote = useDonationStore((s) => s.setQuote);

  useEffect(() => {
    if (freshQuote.data) setQuote(freshQuote.data);
  }, [freshQuote.data, setQuote]);

  const createDonation = useCreateDonation();
  const initiatePayment = useInitiatePayment();

  const total = draft.authoritativeTotal();
  const purposeOption = config?.purposes.find((p) => p.code === draft.purpose);
  const purposeLabel = purposeOption ? t(purposeOption.label) : '—';
  const methodName = methods?.find((m) => m.code === draft.paymentMethodCode)?.name ?? '—';

  const busy = createDonation.isPending || initiatePayment.isPending || freshQuote.isFetching;
  const canConfirm = Boolean(total) && draft.isReadyForReview() && !busy;

  const onConfirm = async () => {
    if (!canConfirm) return;
    try {
      const donation = await createDonation.mutateAsync(toCreateRequest(draft));
      const intent = await initiatePayment.mutateAsync({
        donationId: donation.id,
        paymentMethodCode: draft.paymentMethodCode!,
        walletMsisdn:
          draft.paymentMethodCode === 'easypaisa' ? draft.walletMsisdn ?? undefined : undefined,
        // Where a hosted page (Stripe, JazzCash) sends the donor back to.
        // The server accepts only this site, whatever is sent.
        returnUrl: paymentReturnUrl(),
      });
      // A payment finished somewhere else — on the donor's phone or on a
      // provider's page — is not done when it starts. Wait for the provider
      // rather than showing a receipt for money that has not moved.
      if (intent.status === 'PROCESSING' || (intent.status === 'PENDING' && !intent.instructions)) {
        navigation.navigate('PaymentPending', {
          paymentId: intent.paymentId,
          method: draft.paymentMethodCode!,
          donationId: donation.id,
          publicReference: donation.publicReference,
          amount: donation.totalAmount,
          instrumentLabel: intent.instrumentLabel,
          simulated: intent.simulated,
          redirectUrl: intent.redirectUrl,
          testMode: intent.testMode,
        });
        return;
      }
      navigation.navigate('ThankYou', {
        donationId: donation.id,
        publicReference: donation.publicReference,
      });
    } catch {
      // The error surfaces below; the draft is intentionally preserved so the
      // donor can retry without re-entering anything.
    }
  };

  const failure = createDonation.error ?? initiatePayment.error;

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-review"
      subHeader={<CheckoutHeader current={4} title={t('Review Your Donation')} subtitle={t('Please review your details before confirming')} />}
      pinnedFooter={
        <View style={styles.pinned}>
          <View style={styles.totalBar}>
            <Glyph name="privacy" size={18} />
            <Text variant="bodyStrong" tone="brand" style={styles.totalLabel}>{t('Total Donation')}</Text>
            <Text variant="amountSmall" tone="brand" testID="review-total">
              {total ? formatMoney(total) : '—'}
            </Text>
          </View>

          <Button
            label={t('Confirm Order')}
            onPress={() => void onConfirm()}
            disabled={!canConfirm}
            loading={busy}
            leadingIcon={<LockIcon />}
            testID="cta-confirm"
          />

          <Text variant="caption" tone="tertiary" align="center">{t('Your donation is secure and encrypted.')}</Text>
        </View>
      }
    >


      {failure ? (
        <View style={styles.failure} accessibilityLiveRegion="assertive" testID="review-error">
          <Text variant="bodyStrong" tone="danger">{t('We could not complete your donation')}</Text>
          <Text variant="caption" tone="secondary">
            {t(failure.message)}
          </Text>
        </View>
      ) : null}

      {draft.meal && draft.peopleCount && draft.quote ? (
        <>
          <DonationSummaryCard
            title={t('Order Details')}
            mealName={pick(draft.meal.name, draft.meal.nameUr)}
            imageUrl={draft.meal.imageUrl}
            peopleCount={draft.peopleCount}
            unitPrice={draft.quote.unitPrice}
            totalAmount={draft.quote.totalAmount}
            onEdit={() => navigation.navigate('DonateMeal')}
            testID="review-order"
          />

          <Card style={styles.card}>
            <CardRow label={t('People')} value={formatPeople(draft.peopleCount)} />
            <CardDivider />
            <CardRow label={t('Price Per Person')} value={formatMoney(draft.quote.unitPrice)} />
            <CardDivider />
            <CardRow label={t('Purpose')} value={purposeLabel} />
            {draft.purposeDetails ? (
              <>
                <CardDivider />
                <CardRow label={t('Details')} value={draft.purposeDetails} />
              </>
            ) : null}
            {/* The fee is shown as its own line wherever the total appears —
                a donor should never find an amount they cannot account for. */}
            {draft.quote.deliveryFee.amountMinor > 0 ? (
              <>
                <CardDivider />
                <CardRow label={t('Meals')} value={formatMoney(draft.quote.mealSubtotal)} />
                <CardDivider />
                <CardRow
                  label={t('Doorstep delivery')}
                  value={formatMoney(draft.quote.deliveryFee)}
                />
              </>
            ) : null}
            <CardDivider />
            <CardRow label={t('Total Amount')} value={formatMoney(draft.quote.totalAmount)} emphasis />
          </Card>
        </>
      ) : null}

      <Card
        title={t('Donor Information')}
        style={styles.card}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            size="md"
            fullWidth={false}
            onPress={() => navigation.navigate('DonorInformation')}
            testID="edit-donor"
          />
        }
      >
        <InfoRow icon="person" value={draft.donor?.fullName ?? '—'} />
        <InfoRow icon="email" value={draft.donor?.email || t('No email provided')} />
        <InfoRow icon="phone" value={draft.donor?.mobileNumber ?? '—'} />
        {draft.donor?.anonymous ? (
          <InfoRow icon="privacy" value="This donation will be anonymous" />
        ) : null}
      </Card>

      <Card
        title={t('Payment Method')}
        style={styles.card}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            size="md"
            fullWidth={false}
            onPress={() => navigation.navigate('Payment')}
            testID="edit-payment"
          />
        }
      >
        <InfoRow icon="card" value={methodName} />
      </Card>

      <Card
        title={t('Distribution Date')}
        style={styles.card}
        action={
          <Button
            label={t('Edit')}
            variant="tertiary"
            size="md"
            fullWidth={false}
            onPress={() => navigation.navigate('OrderDetails')}
            testID="edit-date"
          />
        }
      >
        <InfoRow
          icon="calendar"
          value={draft.distributionDate ? formatDate(draft.distributionDate) : '—'}
        />
      </Card>
    </ScreenShell>
  );
}

function InfoRow({ icon, value }: { icon: string; value: string }) {
  return (
    <View style={styles.infoRow}>
      <Glyph name={icon} size={16} />
      <Text variant="body" style={styles.infoValue}>
        {value}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  subtitle: {
    marginTop: space.xs,
    marginBottom: space.lg,
  },
  card: {
    marginTop: space.lg,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    paddingVertical: space.sm,
  },
  infoValue: {
    flex: 1,
  },
  pinned: {
    gap: space.sm,
  },
  totalBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.bg.brandSubtle,
  },
  totalLabel: {
    flex: 1,
  },
  failure: {
    padding: space.md,
    borderRadius: radius.md,
    backgroundColor: colors.action.dangerSubtle,
    gap: space.xxs,
    marginBottom: space.lg,
  },
});
